import { TABLE_COLUMN_KEY_PATTERN, TABLE_COLUMN_TYPES, TABLE_LIMITS, TABLE_ROW_ID_PATTERN, } from '../types/survey';
import { visibleQuestionIds } from './conditions';
/**
 * The single source of answer validation.
 *
 * The form calls it to decide what to show the person filling in the survey,
 * and the submit route calls it before writing anything — so a client that
 * skips the form cannot store an answer the form would have rejected.
 */
export function validateAnswer(question, value) {
    const config = question.config ?? {};
    // A section is a heading: there is nothing to answer.
    if (question.type === 'section')
        return null;
    if (isEmpty(value)) {
        return question.required ? { code: 'required' } : null;
    }
    switch (question.type) {
        case 'scale':
        case 'number': {
            if (typeof value !== 'number' || Number.isNaN(value))
                return { code: 'expected_number' };
            if (config.min !== undefined && value < config.min) {
                return { code: 'min', params: { min: config.min } };
            }
            if (config.max !== undefined && value > config.max) {
                return { code: 'max', params: { max: config.max } };
            }
            return null;
        }
        case 'short_text':
        case 'long_text': {
            if (typeof value !== 'string')
                return { code: 'expected_text' };
            if (config.maxLength !== undefined && value.length > config.maxLength) {
                return { code: 'max_length', params: { count: config.maxLength } };
            }
            return null;
        }
        case 'single_choice': {
            if (typeof value !== 'string')
                return { code: 'expected_option' };
            return isAllowedOption(config, value) ? null : { code: 'unknown_option' };
        }
        case 'multi_choice': {
            if (!Array.isArray(value))
                return { code: 'expected_options' };
            // A table is an array too, so a table's rows are not a list of choices.
            const picked = value;
            if (picked.some((entry) => typeof entry !== 'string' || !isAllowedOption(config, entry))) {
                return { code: 'unknown_option' };
            }
            if (new Set(picked).size !== picked.length)
                return { code: 'distinct_options' };
            if (config.minSelect !== undefined && picked.length < config.minSelect) {
                return { code: 'min_select', params: { count: config.minSelect } };
            }
            if (config.maxSelect !== undefined && picked.length > config.maxSelect) {
                return { code: 'max_select', params: { count: config.maxSelect } };
            }
            return null;
        }
        case 'boolean':
            return typeof value === 'boolean' ? null : { code: 'expected_boolean' };
        case 'date': {
            if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                return { code: 'expected_date' };
            }
            return Number.isNaN(Date.parse(value)) ? { code: 'invalid_date' } : null;
        }
        case 'file': {
            if (!Array.isArray(value))
                return { code: 'expected_files' };
            if (config.maxFiles !== undefined && value.length > config.maxFiles) {
                return { code: 'max_files', params: { count: config.maxFiles } };
            }
            return null;
        }
        case 'table':
            return firstTableError(tableErrors(question, value, 'complete'));
        default:
            return exhaustive(question.type);
    }
}
/**
 * Everything wrong with a table answer, so the form can mark every cell at once
 * and the API can name the first.
 *
 * `shape` is for a save along the way: the structure and every filled cell must
 * be valid, but a missing required cell is not an error yet, because the person
 * is still typing. `complete` is for a submit and also asks for the required ones.
 * Both hold the line on what a client must never be able to do: remove or alter
 * the rows the author fixed, or add what the table does not allow.
 */
export function tableErrors(question, value, mode) {
    const config = question.config ?? {};
    const columns = config.columns ?? [];
    const seeds = config.seedRows ?? [];
    const maxRows = effectiveMaxRows(config);
    const errors = [];
    const at = (row_id = null, column = null) => ({ row_id, column });
    if (value === null || value === undefined)
        return [];
    if (!Array.isArray(value))
        return [{ code: 'expected_rows', cell: at() }];
    if (value.length > maxRows) {
        errors.push({ code: 'max_rows', params: { count: maxRows }, cell: at() });
        return errors;
    }
    const seen = new Set();
    const seedById = new Map(seeds.map((seed) => [seed.row_id, seed]));
    const columnByKey = new Map(columns.map((column) => [column.key, column]));
    for (const entry of value) {
        if (!isRowShape(entry)) {
            errors.push({ code: 'invalid_row', cell: at() });
            continue;
        }
        const { row_id: rowId, values } = entry;
        if (seen.has(rowId)) {
            errors.push({ code: 'duplicate_row', cell: at(rowId) });
            continue;
        }
        seen.add(rowId);
        const seed = seedById.get(rowId);
        if (!seed && config.allowNewRows === false) {
            errors.push({ code: 'new_rows_not_allowed', cell: at(rowId) });
            continue;
        }
        for (const key of Object.keys(values)) {
            if (!columnByKey.has(key))
                errors.push({ code: 'unknown_column', cell: at(rowId, key) });
        }
        for (const column of columns) {
            const cell = values[column.key] ?? null;
            // A fixed cell of a fixed row is the author's, whatever the client sent.
            if (seed && column.lockedOnSeed) {
                if (!sameCell(cell, seed.values[column.key] ?? null)) {
                    errors.push({ code: 'seed_locked', cell: at(rowId, column.key) });
                }
                continue;
            }
            const problem = validateAnswer(cellQuestion(column, mode === 'complete'), cell);
            if (problem)
                errors.push({ ...problem, cell: at(rowId, column.key) });
        }
    }
    for (const seed of seeds) {
        if (!seen.has(seed.row_id))
            errors.push({ code: 'seed_missing', cell: at(seed.row_id) });
    }
    return errors;
}
/** The single error a caller that reports one at a time should show. */
function firstTableError(errors) {
    if (errors.length === 0)
        return null;
    const { code, params, cell } = errors[0];
    return params ? { code, params, cell } : { code, cell };
}
/** For a save along the way: structure and filled cells only. */
export function validateTableShape(question, value) {
    return firstTableError(tableErrors(question, value, 'shape'));
}
export function effectiveMaxRows(config) {
    const asked = config.maxRows ?? TABLE_LIMITS.defaultMaxRows;
    return Math.min(Math.max(asked, 1), TABLE_LIMITS.maxRowsCap);
}
/** A cell checked as the question it would be on its own, with the same rules. */
function cellQuestion(column, required) {
    return {
        id: column.key,
        order_index: 0,
        code: null,
        type: column.type,
        label: column.label,
        description: null,
        required: required && column.required === true,
        config: {
            options: column.options,
            minSelect: column.minSelect,
            maxSelect: column.maxSelect,
            // Bounded even when the author set no bound: an infinite number would be
            // stored as null and read back as an answer nobody gave.
            min: column.min ?? -TABLE_LIMITS.maxNumber,
            max: column.max ?? TABLE_LIMITS.maxNumber,
            maxLength: Math.min(column.maxLength ?? TABLE_LIMITS.maxTextLength, TABLE_LIMITS.maxTextLength),
        },
    };
}
function isRowShape(entry) {
    if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
        return false;
    const row = entry;
    return (typeof row.row_id === 'string' &&
        TABLE_ROW_ID_PATTERN.test(row.row_id) &&
        typeof row.values === 'object' &&
        row.values !== null &&
        !Array.isArray(row.values));
}
function sameCell(a, b) {
    const normal = (value) => value === null || value === undefined || value === '' ? null : value;
    return JSON.stringify(normal(a)) === JSON.stringify(normal(b));
}
/**
 * Whether a table question's own definition is sound: what an author writes
 * when building it, checked before it is stored. A half-written choice column is
 * still allowed, as for any question — this is about what would make an answer
 * impossible or unsafe to store, not about completeness.
 */
export function tableConfigError(config) {
    const columns = config?.columns;
    if (!Array.isArray(columns) || columns.length === 0)
        return 'a table needs at least one column';
    if (columns.length > TABLE_LIMITS.maxColumns) {
        return `a table has at most ${TABLE_LIMITS.maxColumns} columns`;
    }
    const keys = new Set();
    for (const [index, column] of columns.entries()) {
        const name = `column ${index + 1}`;
        if (typeof column !== 'object' || column === null)
            return `${name} is not an object`;
        if (typeof column.key !== 'string' || !TABLE_COLUMN_KEY_PATTERN.test(column.key)) {
            return `${name} needs a key of lowercase letters, digits and underscores, starting with a letter`;
        }
        if (keys.has(column.key))
            return `${name} repeats the key "${column.key}"`;
        keys.add(column.key);
        if (typeof column.label !== 'string' || !column.label.trim())
            return `${name} needs a label`;
        if (column.label.length > TABLE_LIMITS.maxLabelLength)
            return `${name} has a label that is too long`;
        if (!TABLE_COLUMN_TYPES.includes(column.type)) {
            return `${name} has an unsupported type: a column is one of ${TABLE_COLUMN_TYPES.join(', ')}`;
        }
        // A choice column has to say what the choices are: with none declared a cell
        // would accept any text of any length, and a public link lets a stranger write one.
        if (column.type === 'single_choice' || column.type === 'multi_choice') {
            const options = column.options;
            if (!Array.isArray(options) || options.length === 0)
                return `${name} needs its options`;
            if (options.length > TABLE_LIMITS.maxOptions) {
                return `${name} has more than ${TABLE_LIMITS.maxOptions} options`;
            }
            if (options.some((option) => typeof option?.value !== 'string' ||
                typeof option?.label !== 'string' ||
                !option.value ||
                option.value.length > TABLE_LIMITS.maxTextLength)) {
                return `${name} has invalid options`;
            }
        }
    }
    if (config?.maxRows !== undefined) {
        if (!Number.isInteger(config.maxRows) || config.maxRows < 1 || config.maxRows > TABLE_LIMITS.maxRowsCap) {
            return `maxRows must be a whole number from 1 to ${TABLE_LIMITS.maxRowsCap}`;
        }
    }
    if (config?.allowNewRows !== undefined && typeof config.allowNewRows !== 'boolean') {
        return 'allowNewRows must be true or false';
    }
    const seeds = config?.seedRows ?? [];
    if (!Array.isArray(seeds))
        return 'seedRows must be a list';
    if (seeds.length > effectiveMaxRows(config ?? {})) {
        return `there are more seed rows than the table allows (${effectiveMaxRows(config ?? {})})`;
    }
    // The seed rows are an answer in their own right: the same rules, with nothing
    // required yet, since filling them in is what the person answering will do.
    const probe = {
        id: 'seed',
        order_index: 0,
        code: null,
        type: 'table',
        label: 'seed',
        description: null,
        required: false,
        config: { ...config, seedRows: [], allowNewRows: true },
    };
    const problem = firstTableError(tableErrors(probe, seeds, 'shape'));
    return problem ? `the seed rows are not valid (${problem.code})` : null;
}
/** Validates a whole submission; an empty result means it can be submitted. */
export function validateSubmission(questions, answers) {
    const errors = [];
    // What is not asked cannot be wrong or missing, whatever is stored for it.
    const asked = visibleQuestionIds(questions, answers);
    for (const question of questions) {
        if (!asked.has(question.id))
            continue;
        const error = validateAnswer(question, answers[question.id] ?? null);
        if (error)
            errors.push({ question_id: question.id, ...error });
    }
    return errors;
}
/** Which column an answer of this type is stored in. */
export function answerColumn(type) {
    switch (type) {
        case 'scale':
        case 'number':
        case 'boolean':
            return 'number';
        case 'multi_choice':
        case 'file':
        case 'table':
            return 'json';
        // Never stored, but the switch has to name it.
        case 'section':
        default:
            return 'text';
    }
}
/** Whether an answer says nothing: missing, blank text or an empty list. */
export function isEmpty(value) {
    if (value === null || value === undefined)
        return true;
    if (typeof value === 'string')
        return value.trim() === '';
    if (Array.isArray(value))
        return value.length === 0;
    return false;
}
/** How many of the questions have an answer, by the same rule the validator uses. */
export function countAnswered(questions, answers) {
    return questions.filter((question) => !isEmpty(answers[question.id])).length;
}
function isAllowedOption(config, value) {
    // A choice question with no options declared accepts any value: the builder
    // is free to save a half-finished question as a draft.
    if (!config.options || config.options.length === 0)
        return true;
    // Compared as text: an option written as { value: 4 } is what a menu sends back as "4".
    return config.options.some((option) => String(option.value) === value);
}
function exhaustive(value) {
    throw new Error(`Unhandled question type: ${String(value)}`);
}
/**
 * How many answers an anonymous survey needs before any is shown.
 *
 * With three people the answer that differs has an obvious author, and with
 * four it is a short guess. Five is the smallest group in which no single
 * answer is one person's by elimination — still small, so the survey page tells
 * the operator when the workspace has fewer people than this.
 */
export const ANONYMITY_THRESHOLD = 5;
/**
 * What an anonymous survey may not contain. An attachment is stored with the
 * person who uploaded it, so a file question would put the identity back next to
 * the answer.
 */
export function anonymousSurveyError(questions) {
    return questions.some((question) => typeof question === 'object' && question !== null && question.type === 'file')
        ? 'An anonymous survey cannot have file questions: an attachment is tied to the person who uploaded it'
        : null;
}
/**
 * What a public survey may not contain. Anyone on the internet could attach
 * anything, and it would land in the storage bucket.
 */
export function publicSurveyError(questions) {
    return questions.some((question) => typeof question === 'object' && question !== null && question.type === 'file')
        ? 'A public survey cannot have file questions: anyone could upload anything to the storage'
        : null;
}
